import { toast } from "sonner";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/features/auth/hooks/useAuth";
import {
  PURCHASE_POINTS_BALANCE_CHANGED_MESSAGE,
  PURCHASE_POINTS_BALANCE_SCOPE,
  PURCHASE_POINTS_CONVERSIONS_SCOPE,
  PURCHASE_POINTS_CONVERT_ERROR_MESSAGE,
} from "@/features/purchasePoints/config/purchasePoints.config";
import { isBusinessCode, toPurchasePointsError } from "@/features/purchasePoints/errors/purchasePointsError";
import { purchasePointsService } from "@/features/purchasePoints/services/purchasePoints.service";

/**
 * Converting the whole available balance. A bare mutation rather than the
 * shared read policy, which covers queries only.
 *
 * `isPending` locks the button, but that is an interface guard, not the
 * guarantee: what makes a double click harmless is the server refusing the
 * second one.
 */
export const usePurchasePointsConversion = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  // The same identity the readings key their cache on: invalidating with an
  // undefined one would match nothing and leave the screen stale, silently.
  const employeeId = user?.employee?.id ?? null;

  const refreshBalance = () => {
    void queryClient.invalidateQueries({ queryKey: [PURCHASE_POINTS_BALANCE_SCOPE, employeeId] });
  };

  const {
    mutate, isPending, isSuccess, data, reset,
  } = useMutation({
    mutationFn: () => purchasePointsService.createConversion(),
    onSuccess: () => {
      refreshBalance();
      void queryClient.invalidateQueries({ queryKey: [PURCHASE_POINTS_CONVERSIONS_SCOPE, employeeId] });
    },
    onError: (error) => {
      // An empty balance is a screen out of date, not a breakdown: the balance
      // is refreshed and said plainly, where a failure is announced as one.
      if (isBusinessCode(toPurchasePointsError(error).code)) {
        refreshBalance();
        toast.info(PURCHASE_POINTS_BALANCE_CHANGED_MESSAGE);
        return;
      }

      toast.error(PURCHASE_POINTS_CONVERT_ERROR_MESSAGE);
    },
  });

  return {
    convertPoints: mutate,
    isPending,
    isSuccess,
    conversion: data ?? null,
    reset,
  };
};
